import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDiagnosticStore, useAuthStore } from '@shared/state';
import { loadProfile } from '@shared/services/profile';
import type { CogniPrintProfile } from '@shared/types/cogniprint';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import { CogniPrintChart } from '../components/profile/CogniPrintChart';
import { Button } from '../components/ui/Button';

export function ProfileRevealPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const storeProfile = useDiagnosticStore((s) => s.profile);
  const [profile, setProfile] = useState<CogniPrintProfile | null>(storeProfile);
  const [loading, setLoading] = useState(!storeProfile);

  // If store is empty (e.g. page refresh), load from Firestore
  useEffect(() => {
    if (profile || !user) return;
    loadProfile(user.uid)
      .then((p) => {
        if (p) {
          setProfile(p);
        } else {
          navigate('/diagnostic?mode=quick');
        }
      })
      .catch(() => navigate('/diagnostic?mode=quick'))
      .finally(() => setLoading(false));
  }, [user, profile, navigate]);

  if (!user) {
    navigate('/login');
    return null;
  }

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isQuick = profile.diagnosticType === 'quick';

  return (
    <div className="min-h-screen bg-dark-bg relative overflow-hidden">
      <RidgeTexture opacity="opacity-[0.08]" />

      <div className="relative z-10 max-w-[640px] mx-auto px-4 py-12">
        {/* ThumbsUpCog + Headline */}
        <motion.img
          src="/ThumbsUpCog.png"
          alt="Professor Cogni celebrating"
          className="w-20 h-20 mx-auto mb-4 object-contain"
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        />
        <motion.h1
          className="font-heading font-bold text-3xl sm:text-4xl text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Your <span className="text-gold-500">CogniPrint</span>
        </motion.h1>

        {/* Dimension bars */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <CogniPrintChart
            profile={profile}
            variant="reveal"
            animated
            isFree={user.subscription === 'free'}
          />
        </motion.div>

        {/* Upgrade CTA for free tier */}
        {isQuick && (
          <motion.div
            className="rounded-lg border border-dark-border bg-dark-surface p-6 mb-8 text-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <p className="text-sm text-content-secondary font-body mb-4">
              {profile.summary?.upgradeHook ||
                'This quick profile mapped 4 dimensions of how you learn. Unlock all 8 for a learning experience built for exactly how your brain works.'}
            </p>
            <Button
              variant="gold"
              onClick={() => navigate('/pricing')}
            >
              Unlock Your Full CogniPrint
            </Button>
          </motion.div>
        )}

        {/* Summary */}
        {profile.summary && (
          <motion.div
            className="mb-10"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
          >
            {profile.summary.descriptiveLabel && (
              <p className="text-xs font-body font-semibold tracking-[0.08em] uppercase text-indigo-400 mb-2">
                {profile.summary.descriptiveLabel}
              </p>
            )}
            <p className="text-lg text-content-secondary font-body leading-relaxed">
              {profile.summary.summaryText}
            </p>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          className="flex flex-col sm:flex-row items-center gap-4 justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <Button size="lg" onClick={() => navigate('/dashboard')}>
            Continue to Dashboard
          </Button>
          <button
            onClick={() => {
              useDiagnosticStore.getState().reset();
              navigate('/diagnostic?mode=quick');
            }}
            className="text-sm text-content-muted hover:text-content transition-colors cursor-pointer"
          >
            Retake Diagnostic
          </button>
        </motion.div>
      </div>
    </div>
  );
}
